import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    getClientById,
    updateClient,
    createClient,
    fetchSegmentos,
    fetchBasesList,
    fetchVendedoresList,
    fetchEstados,
    getClientStats,
    getClientProducts,
    getClientOrders,
    Client,
    Segmento,
    BaseOption,
    VendedorOption,
    Estado,
    ClientStats,
    ClientProduct,
    ClientOrder,
} from '../services/clientsAPI';
import { fetchProductDetails } from '../../products/services/productsAPI';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import EditIcon from '@mui/icons-material/Edit';
import styles from './ClientDetailModal.module.css';

// ── Interfaces ──
interface ClientDetailModalProps {
    clientId: number | null;
    onClose: () => void;
    onSuccess: () => void;
}

interface ProductWithImage {
    item: string;
    linha: string;
    totalQuantity: number;
    totalRevenue: number;
    img?: string;
    descricao?: string;
}

// ── Formatters ──
const formatCurrency = (value: any) => {
    if (value === null || value === undefined || value === '') return 'R$ 0,00';
    const num = Number(value);
    if (isNaN(num)) return 'R$ 0,00';
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatNumber = (value: any) => {
    if (value === null || value === undefined || value === '') return '0';
    const num = Number(value);
    if (isNaN(num)) return '0';
    return num.toLocaleString('pt-BR');
};

const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ── Initial state ──
const initialClientState: Partial<Client> = {
    NOME: '', FANTASIA: '', TIPOPESSOA: 'J', DOCUMENTO: '',
    ENDERECO: '', NUMERO: '', COMPLEMENTO: '', BAIRRO: '',
    CEP: '', CIDADE: '', ESTADO: '', PAIS: 'Brasil',
    TELEFONE: '', DDD: '', EMAIL: '', SITE: '',
    ATIVO: 'A', OBS: '', SEGMENTO: 0, LIMITE: 0,
    IBGE: '', SUPORTE: '', RC: '', VENDEDOR: 0,
    TIPO: '', CNPJ_GRUPO: ''
};

const REPORT_DAYS = 90;

const ClientDetailModal: React.FC<ClientDetailModalProps> = ({ clientId, onClose, onSuccess }) => {
    // ── Form state ──
    const [formData, setFormData] = useState<Partial<Client>>(initialClientState);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(clientId === null);
    const [isAntTheme, setIsAntTheme] = useState<boolean>(true);

    const isRestrictedField = true;

    // ── Aux data ──
    const [segmentos, setSegmentos] = useState<Segmento[]>([]);
    const [basesList, setBasesList] = useState<BaseOption[]>([]);
    const [vendedoresList, setVendedoresList] = useState<VendedorOption[]>([]);
    const [estadosList, setEstadosList] = useState<Estado[]>([]);
    const [citiesList, setCitiesList] = useState<{nome: string}[]>([]);

    // ── Report state ──
    const reportRef = useRef<HTMLDivElement>(null);
    const [reportLevel, setReportLevel] = useState<0 | 1 | 2>(0); // 0=nothing, 1=stats+products, 2=+orders
    const [reportLoading, setReportLoading] = useState(false);
    const [reportError, setReportError] = useState<string | null>(null);
    const [stats, setStats] = useState<ClientStats | null>(null);
    const [products, setProducts] = useState<ProductWithImage[]>([]);
    const [orders, setOrders] = useState<ClientOrder[]>([]);
    const [ordersTotal, setOrdersTotal] = useState(0);
    const [productImagesMap, setProductImagesMap] = useState<Record<string, string>>({});
    const [productNamesMap, setProductNamesMap] = useState<Record<string, string>>({});

    // ── Load aux data ──
    useEffect(() => {
        const loadAuxData = async () => {
            try {
                const [segRes, basesRes, vendRes, estRes] = await Promise.all([
                    fetchSegmentos(),
                    fetchBasesList(),
                    fetchVendedoresList(),
                    fetchEstados()
                ]);
                setSegmentos(segRes.data);
                setBasesList(basesRes.data);
                setVendedoresList(vendRes.data);
                setEstadosList(estRes.data);
            } catch (err) {
                console.error("Erro ao carregar listas de apoio", err);
            }
        };
        loadAuxData();
    }, []);

    // ── ESC to close ──
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    // ── Load client details ──
    const loadClientDetails = useCallback(async () => {
        if (!clientId) return;
        try {
            setLoading(true);
            const response = await getClientById(clientId);
            setFormData(response.data);
            setError(null);
            setIsEditing(false);
        } catch (err) {
            setError('Erro ao carregar detalhes do cliente.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => {
        // Reset report when client changes
        setReportLevel(0);
        setStats(null);
        setProducts([]);
        setOrders([]);
        setReportError(null);

        if (clientId) {
            loadClientDetails();
        } else {
            setFormData(initialClientState);
            setIsEditing(true);
            setLoading(false);
        }
    }, [clientId, loadClientDetails]);


    // Buscar cidades do IBGE quando o ESTADO mudar
    useEffect(() => {
        if (formData.ESTADO && formData.ESTADO.length === 2) {
            fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${formData.ESTADO}/municipios`)
                .then(res => res.json())
                .then(data => {
                    if (Array.isArray(data)) {
                        const sortedCities = data.map((c: any) => ({ nome: c.nome.toUpperCase() })).sort((a: any, b: any) => a.nome.localeCompare(b.nome));
                        setCitiesList(sortedCities);
                    }
                })
                .catch(err => {
                    console.error("Erro ao buscar cidades do IBGE", err);
                    setCitiesList([]);
                });
        } else {
            setCitiesList([]);
        }
    }, [formData.ESTADO]);

    // ── Handlers ──
    const handleCepBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        if (!isEditing) return;
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length === 8) {
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
                const data = await response.json();
                if (!data.erro) {
                    setFormData(prev => ({
                        ...prev,
                        ENDERECO: data.logradouro.toUpperCase(),
                        BAIRRO: data.bairro.toUpperCase(),
                        ESTADO: data.uf,
                        CIDADE: data.localidade.toUpperCase(),
                        IBGE: data.ibge,
                        PAIS: 'BRASIL'
                    }));
                }
            } catch (error) {
                console.error("Erro ao buscar CEP:", error);
            }
        }
    };
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        if (!formData.NOME) {
            alert('O campo Razão Social é obrigatório.');
            return;
        }
        try {
            setSaving(true);
            if (clientId) {
                await updateClient(clientId, formData);
                alert('Cliente atualizado com sucesso!');
            } else {
                await createClient(formData);
                alert('Cliente criado com sucesso!');
            }
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error(err);
            alert('Erro ao salvar: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    // ── Report: Load Level 1 (stats + products) ──
    const loadReportLevel1 = async () => {
        if (!clientId) return;
        try {
            setReportLoading(true);
            setReportError(null);

            // Date range: last N days from DATA_PEDIDO (or today if not available)
            const lastPurchase = formData.DATA_PEDIDO ? new Date(formData.DATA_PEDIDO) : new Date();
            const dataF = lastPurchase.toISOString().split('T')[0];
            const dataI_date = new Date(lastPurchase);
            dataI_date.setDate(dataI_date.getDate() - REPORT_DAYS);
            const dataI = dataI_date.toISOString().split('T')[0];

            const [statsRes, productsRes] = await Promise.all([
                getClientStats(clientId, dataI, dataF),
                getClientProducts(clientId, dataI, dataF),
            ]);

            setStats(statsRes.data);

            const rawProducts: ClientProduct[] = productsRes.data;

            const imgMap: Record<string, string> = {};
            const nameMap: Record<string, string> = {};

            // Fetch product images/names for these products
            if (rawProducts.length > 0) {
                const refs = rawProducts.map(p => p.item);
                const results = await Promise.all(
                    refs.map(ref =>
                        fetchProductDetails(ref)
                            .then(res => ({
                                ref,
                                img: (res.data as any).img as string | null,
                                descricao: (res.data as any).DESCRICAO as string | null,
                            }))
                            .catch(() => ({ ref, img: null, descricao: null }))
                    )
                );
                results.forEach(r => {
                    if (r.img) imgMap[r.ref] = r.img;
                    if (r.descricao) nameMap[r.ref] = r.descricao;
                });
                setProductImagesMap(imgMap);
                setProductNamesMap(nameMap);
            }

            // Merge product data with images
            const mergedProducts: ProductWithImage[] = rawProducts.map(p => ({
                ...p,
                img: imgMap[p.item] || productImagesMap[p.item],
                descricao: nameMap[p.item] || productNamesMap[p.item],
            }));
            setProducts(mergedProducts);
            setReportLevel(1);
            setTimeout(() => {
                reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        } catch (err: any) {
            console.error('Erro ao carregar relatório:', err);
            setReportError('Erro ao carregar relatório do cliente.');
        } finally {
            setReportLoading(false);
        }
    };

    // ── Report: Load Level 2 (orders) ──
    const loadReportLevel2 = async () => {
        if (!clientId) return;
        try {
            setReportLoading(true);
            setReportError(null);

            const lastPurchase = formData.DATA_PEDIDO ? new Date(formData.DATA_PEDIDO) : new Date();
            const dataF = lastPurchase.toISOString().split('T')[0];
            const dataI_date = new Date(lastPurchase);
            dataI_date.setDate(dataI_date.getDate() - REPORT_DAYS);
            const dataI = dataI_date.toISOString().split('T')[0];

            const ordersRes = await getClientOrders(clientId, dataI, dataF, 1, 50);
            setOrders(ordersRes.data.orders);
            setOrdersTotal(ordersRes.data.pagination.totalItems);
            setReportLevel(2);
            setTimeout(() => {
                reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        } catch (err: any) {
            console.error('Erro ao carregar pedidos:', err);
            setReportError('Erro ao carregar pedidos do cliente.');
        } finally {
            setReportLoading(false);
        }
    };

    // ── Report date range display ──
    const getReportDateRange = () => {
        const lastPurchase = formData.DATA_PEDIDO ? new Date(formData.DATA_PEDIDO) : new Date();
        const dataF = formatDate(formData.DATA_PEDIDO);
        const dataI_date = new Date(lastPurchase);
        dataI_date.setDate(dataI_date.getDate() - REPORT_DAYS);
        const dataI = formatDate(dataI_date.toISOString().split('T')[0]);
        return `${dataI} → ${dataF}`;
    };

    // ── Render helpers ──
    const renderInput = (label: string, name: keyof Client, type: string = "text", readOnlyOverride: boolean = false, onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void) => (
        <div className={styles.formGroup}>
            <label className={styles.formLabel}>{label}</label>
            <input
                type={type}
                name={name}
                className={styles.formInput}
                value={formData[name] ? String(formData[name]) : ''}
                onChange={handleChange}
                onBlur={onBlur}
                readOnly={!isEditing || readOnlyOverride}
                maxLength={255}
            />
        </div>
    );

    const renderReadonlyField = (label: string, value: string) => (
        <div className={styles.formGroup}>
            <label className={styles.formLabel}>{label}</label>
            <input type="text" className={styles.formInput} value={value} readOnly />
        </div>
    );

    // ── Loading / Error states ──
    if (loading) return <div className={styles.modalOverlay}><LoadingSpinner message="Carregando..." /></div>;
    if (error && !isEditing) return <div className={styles.modalOverlay} onClick={onClose}><div className={styles.errorMessage}>{error}</div></div>;

    // ── Compute report period ──
    const reportPeriodLabel = getReportDateRange();
    const hasLastPurchase = !!formData.DATA_PEDIDO;

    return (
        <div className={styles.modalOverlay} onClick={handleBackdropClick}>
            <div className={`${styles.modalContainer} ${isAntTheme ? styles.antTheme : styles.modernTheme}`}>
                {/* ── Header ── */}
                <div className={styles.highlightSection}>
                    <div className={styles.highlightHeader}>
                        <h1 className={styles.highlightTitle}>
                            {clientId ? (formData.FANTASIA || formData.NOME) : 'Novo Cliente'}
                            {clientId && <span className={styles.clientIdTag}>#{clientId}</span>}
                        </h1>
                        <p className={styles.highlightSubtitle}>{formData.NOME || 'Novo Cadastro'}</p>
                    </div>
                    <div className={styles.highlightInfo}>
                        {!isEditing ? (
                            <button onClick={() => setIsEditing(true)} className={styles.editButton}>
                                <EditIcon fontSize="small" style={{ fontSize: 16, marginRight: 4 }} />
                                EDITAR
                            </button>
                        ) : (
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={handleSave} disabled={saving} className={styles.saveButton}>{saving ? '...' : 'SALVAR'}</button>
                                {clientId && <button onClick={() => { setIsEditing(false); loadClientDetails(); }} className={styles.cancelButton}>CANCELAR</button>}
                            </div>
                        )}
                        <button className={styles.closeButton} onClick={onClose}>✕</button>
                    </div>
                </div>

                {/* ── Content ── */}
                <div className={styles.contentSection}>
                    <div className={styles.modalContent}>

                        {/* ── Informações do Cliente (Container Único e Compacto) ── */}
                        <div className={styles.clientInfoCard}>

                            {/* 1. Detalhes da Empresa */}
                            <div className={styles.sectionBlock}>
                                <div className={styles.sectionBlockHeader}>
                                    <span className={styles.sectionBlockTitle}>Detalhes da Empresa</span>
                                </div>
                                <div className={styles.formGrid4}>
                                    <div className={styles.colSpan2}>{renderInput("Razão Social", "NOME")}</div>
                                    <div className={styles.colSpan2}>{renderInput("Nome Fantasia", "FANTASIA")}</div>

                                    {renderInput("CPF/CNPJ", "DOCUMENTO")}

                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Tipo Pessoa</label>
                                        <select name="TIPOPESSOA" className={styles.formSelect} value={formData.TIPOPESSOA} onChange={handleChange} disabled={!isEditing}>
                                            <option value="J">Jurídica</option>
                                            <option value="F">Física</option>
                                            <option value="E">Estrangeiro</option>
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Tipo</label>
                                        {isEditing ? (
                                            <select name="TIPO" className={styles.formSelect} value={formData.TIPO || ''} onChange={handleChange}>
                                                <option value="">Selecione...</option>
                                                <option value="REV">Revenda</option>
                                                <option value="CON">Consumidor</option>
                                                <option value="IND">Indústria</option>
                                                <option value="OUT">Outros</option>
                                            </select>
                                        ) : (
                                            <input type="text" className={styles.formInput} value={formData.TIPO || '-'} readOnly />
                                        )}
                                    </div>

                                    {renderInput("CNPJ Grupo", "CNPJ_GRUPO", "text", isRestrictedField)}
                                    {renderInput("IBGE", "IBGE")}
                                    {renderReadonlyField("Data Cadastro", formatDate(formData.DATACAD))}
                                </div>
                            </div>

                            {/* 2. Endereço */}
                            <div className={styles.sectionBlock}>
                                <div className={styles.sectionBlockHeader}>
                                    <span className={styles.sectionBlockTitle}>Endereço</span>
                                </div>
                                <div className={styles.formGrid4}>
                                    {renderInput("CEP", "CEP", "text", false, handleCepBlur)}
                                    <div className={styles.colSpan2}>{renderInput("Endereço", "ENDERECO")}</div>
                                    {renderInput("Número", "NUMERO")}
                                    {renderInput("Complemento", "COMPLEMENTO")}
                                    {renderInput("Bairro", "BAIRRO")}
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Cidade</label>
                                        {isEditing && citiesList.length > 0 ? (
                                            <select name="CIDADE" className={styles.formSelect} value={formData.CIDADE || ''} onChange={handleChange}>
                                                <option value="">Selecione a cidade...</option>
                                                {citiesList.map(city => <option key={city.nome} value={city.nome}>{city.nome}</option>)}
                                            </select>
                                        ) : isEditing ? (
                                            <input type="text" name="CIDADE" className={styles.formInput} value={formData.CIDADE || ''} onChange={handleChange} maxLength={255} placeholder="Digite a cidade..." />
                                        ) : (
                                            <input type="text" className={styles.formInput} value={formData.CIDADE || '-'} readOnly />
                                        )}
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>UF</label>
                                        {isEditing ? (
                                            <select name="ESTADO" className={styles.formSelect} value={formData.ESTADO || ''} onChange={handleChange}>
                                                <option value="">--</option>
                                                {estadosList.map(est => <option key={est.ESTADO} value={est.ESTADO}>{est.ESTADO}</option>)}
                                            </select>
                                        ) : (
                                            <input type="text" className={styles.formInput} value={formData.ESTADO || '-'} readOnly />
                                        )}
                                    </div>
                                    {renderInput("País", "PAIS")}
                                </div>
                            </div>

                            {/* 3. Contato */}
                            <div className={styles.sectionBlock}>
                                <div className={styles.sectionBlockHeader}>
                                    <span className={styles.sectionBlockTitle}>Contato</span>
                                </div>
                                <div className={styles.formGrid4}>
                                    {renderInput("DDD", "DDD")}
                                    {renderInput("Telefone", "TELEFONE")}
                                    <div className={styles.colSpan2}>{renderInput("Email", "EMAIL", "email")}</div>
                                    {renderInput("Site", "SITE")}
                                </div>
                            </div>

                            {/* 4. Dados Comerciais */}
                            <div className={styles.sectionBlock}>
                                <div className={styles.sectionBlockHeader}>
                                    <span className={styles.sectionBlockTitle}>Dados Comerciais</span>
                                </div>
                                <div className={styles.formGrid4}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Status</label>
                                        <select name="ATIVO" className={styles.formSelect} value={formData.ATIVO} onChange={handleChange} disabled={!isEditing}>
                                            <option value="A">Ativo</option>
                                            <option value="I">Inativo</option>
                                            <option value="P">Prospect</option>
                                            <option value="D">Desativado</option>
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Segmento</label>
                                        <select
                                            name="SEGMENTO"
                                            className={styles.formSelect}
                                            value={formData.SEGMENTO || 0}
                                            onChange={handleChange}
                                            disabled={true}
                                            
                                        >
                                            <option value="0">Selecione...</option>
                                            {segmentos.map(seg => <option key={seg.ID} value={seg.ID}>{seg.DESCRICAO}</option>)}
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Vendedor</label>
                                        <select
                                            name="VENDEDOR"
                                            className={styles.formSelect}
                                            value={formData.VENDEDOR || 0}
                                            onChange={handleChange}
                                            disabled={true}
                                            
                                        >
                                            <option value="0">Selecione...</option>
                                            {vendedoresList.map(vend => <option key={vend.ID} value={vend.ID}>{vend.NOME}</option>)}
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Suporte</label>
                                        <select
                                            name="SUPORTE"
                                            className={styles.formSelect}
                                            value={formData.SUPORTE || 0}
                                            onChange={handleChange}
                                            disabled={true}
                                            
                                        >
                                            <option value="0">Selecione...</option>
                                            {basesList.map(base => <option key={base.ID} value={base.ID}>{base.BASE}</option>)}
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>RC</label>
                                        {isEditing ? (
                                            <select name="RC" className={styles.formSelect} value={formData.RC || ''} onChange={handleChange}>
                                                <option value="">Selecione...</option>
                                                <option value="R">R</option>
                                                <option value="C">C</option>
                                            </select>
                                        ) : (
                                            <input type="text" className={styles.formInput} value={formData.RC || '-'} readOnly />
                                        )}
                                    </div>

                                    {renderInput("Limite de Crédito", "LIMITE", "number", isRestrictedField)}
                                    {renderReadonlyField("Última Compra", formatDate(formData.DATA_PEDIDO))}
                                </div>
                            </div>

                            {/* 5. Observações */}
                            <div className={styles.sectionBlock} style={{ marginBottom: 0 }}>
                                <div className={styles.sectionBlockHeader}>
                                    <span className={styles.sectionBlockTitle}>Observações</span>
                                </div>
                                <textarea name="OBS" className={styles.formInput} value={formData.OBS || ''} onChange={handleChange} readOnly={!isEditing} rows={2} />
                            </div>

                        </div>

                        {/* ── RELATÓRIO DO CLIENTE ── */}
                        {clientId && (
                            <div ref={reportRef} className={styles.reportSection}>
                                <div className={styles.reportHeader}>
                                    <span className={styles.reportTitle}>Relatório do Cliente</span>
                                    {hasLastPurchase && (
                                        <span className={styles.reportPeriod}>
                                            Período: {reportPeriodLabel} (90 dias a partir da última compra)
                                        </span>
                                    )}
                                </div>

                                {/* Level 0: Quick info always visible */}
                                <div className={styles.reportQuickInfo}>
                                    <div className={styles.quickInfoRow}>
                                        <span className={styles.quickInfoLabel}>Última compra registrada:</span>
                                        <span className={styles.quickInfoValue}>{formatDate(formData.DATA_PEDIDO)}</span>
                                    </div>
                                    {!hasLastPurchase && (
                                        <p className={styles.reportNoData}>Sem registro de compras para este cliente.</p>
                                    )}
                                </div>

                                {/* Level 1: Stats + Products */}
                                {reportLevel >= 1 && stats && (
                                    <div className={styles.reportContent}>
                                        {/* Stats cards */}
                                        <div className={styles.statsRow}>
                                            <div className={styles.statCard}>
                                                <span className={styles.statLabel}>Faturamento</span>
                                                <span className={styles.statValue}>{formatCurrency(stats.faturamentoTotal)}</span>
                                            </div>
                                            <div className={styles.statCard}>
                                                <span className={styles.statLabel}>Pedidos</span>
                                                <span className={styles.statValue}>{formatNumber(stats.totalPedidos)}</span>
                                            </div>
                                            <div className={styles.statCard}>
                                                <span className={styles.statLabel}>Pares</span>
                                                <span className={styles.statValue}>{formatNumber(stats.totalPares)}</span>
                                            </div>
                                            <div className={styles.statCard}>
                                                <span className={styles.statLabel}>Ticket Médio</span>
                                                <span className={styles.statValue}>{formatCurrency(stats.ticketMedio)}</span>
                                            </div>
                                        </div>

                                        {/* Products list */}
                                        {products.length > 0 && (
                                            <div className={styles.reportProductsSection}>
                                                <div className={styles.reportSubHeader}>
                                                    <span className={styles.reportSubTitle}>Principais Modelos</span>
                                                    <span className={styles.reportSubCount}>{products.length} produto{products.length !== 1 ? 's' : ''}</span>
                                                </div>
                                                <div className={styles.productList}>
                                                    {products.map((p, i) => {
                                                        const imgSrc = productImagesMap[p.item] || p.img;
                                                        const displayName = productNamesMap[p.item] || p.descricao || p.item;
                                                        return (
                                                            <div key={i} className={styles.productRow}>
                                                                {imgSrc ? (
                                                                    <img src={imgSrc} alt={p.item} className={styles.productImg} />
                                                                ) : (
                                                                    <div className={styles.productImgPlaceholder}>
                                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                                                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                                            <circle cx="8.5" cy="8.5" r="1.5" />
                                                                            <polyline points="21 15 16 10 5 21" />
                                                                        </svg>
                                                                    </div>
                                                                )}
                                                                <div className={styles.productInfo}>
                                                                    <div className={styles.productName}>{displayName}</div>
                                                                    <div className={styles.productRef}>{p.item} · {p.linha}</div>
                                                                </div>
                                                                <div className={styles.productValues}>
                                                                    <div className={styles.productVal}>
                                                                        <div className={styles.productValLabel}>Vendas</div>
                                                                        <div className={styles.productValNum}>{formatCurrency(p.totalRevenue)}</div>
                                                                    </div>
                                                                    <div className={styles.productVal}>
                                                                        <div className={styles.productValLabel}>Pares</div>
                                                                        <div className={styles.productValNum}>{formatNumber(p.totalQuantity)}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Level 2: Orders table */}
                                        {reportLevel >= 2 && orders.length > 0 && (
                                            <div className={styles.reportOrdersSection}>
                                                <div className={styles.reportSubHeader}>
                                                    <span className={styles.reportSubTitle}>Últimos Pedidos</span>
                                                    <span className={styles.reportSubCount}>{ordersTotal} pedido{ordersTotal !== 1 ? 's' : ''}</span>
                                                </div>
                                                <div className={styles.ordersTableWrap}>
                                                    <table className={styles.ordersTable}>
                                                        <thead>
                                                            <tr>
                                                                <th>Pedido</th>
                                                                <th>Emissão</th>
                                                                <th>NF</th>
                                                                <th>Tipo</th>
                                                                <th>Situação</th>
                                                                <th className={styles.rightAlign}>Valor</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {orders.map(o => (
                                                                <tr key={o.ID}>
                                                                    <td className={styles.mono}>#{o.COD_TEOREMA}</td>
                                                                    <td>{formatDate(o.EMISSAO)}</td>
                                                                    <td className={styles.mono}>{o.NOTA_FISCAL || '—'}</td>
                                                                    <td>{o.TIPO_PEDIDO || '—'}</td>
                                                                    <td>
                                                                        <span className={`${styles.statusBadge} ${o.SITUACAO === 'Faturado' ? styles.statusDone : styles.statusPending}`}>
                                                                            {o.SITUACAO}
                                                                        </span>
                                                                    </td>
                                                                    <td className={`${styles.rightAlign} ${styles.mono}`}>
                                                                        {formatCurrency(o.VALOR_TOTAL)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Report error */}
                                {reportError && (
                                    <div className={styles.reportError}>{reportError}</div>
                                )}

                                {/* Report actions */}
                                <div className={styles.reportActions}>
                                    {reportLevel === 0 && hasLastPurchase && (
                                        <button
                                            className={styles.reportBtn}
                                            onClick={loadReportLevel1}
                                            disabled={reportLoading}
                                        >
                                            {reportLoading ? 'Carregando...' : 'Carregar Relatório'}
                                        </button>
                                    )}
                                    {reportLevel === 1 && hasLastPurchase && (
                                        <button
                                            className={styles.reportBtnSecondary}
                                            onClick={loadReportLevel2}
                                            disabled={reportLoading}
                                        >
                                            {reportLoading ? 'Carregando...' : 'Carregar Mais Detalhes (Pedidos)'}
                                        </button>
                                    )}
                                    {reportLevel >= 2 && (
                                        <span className={styles.reportComplete}>Relatório completo carregado</span>
                                    )}
                                    {!hasLastPurchase && (
                                        <span className={styles.reportNoData}>Nenhuma compra registrada para gerar relatório.</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientDetailModal;
